import { type PropsWithChildren, ReactNode } from 'react';
import { ErrorBoundary, type FallbackProps } from 'react-error-boundary';
import { QueryErrorResetBoundary } from '@tanstack/react-query';

import { ApiErrorComponent, GlobalErrorComponent } from '@/shared/ui/Error';

const ApiFallbackRender = <T extends FallbackProps>(
  { resetErrorBoundary, error }: T,
  errorComponent?: ReactNode,
) => {
  const err = error;
  console.log(err);
  // NOTE sentry exception
  if (errorComponent === null || errorComponent) return errorComponent;
  return <ApiErrorComponent onClick={resetErrorBoundary} />;
};

const GlobalFallbackRender = ({ resetErrorBoundary, error }: FallbackProps) => {
  const err = error;
  console.log(err);
  // NOTE sentry exception
  return <GlobalErrorComponent onClick={resetErrorBoundary} />;
};

const ApiErrorBoundary = ({
  children,
  errorComponent,
}: PropsWithChildren<{ errorComponent?: ReactNode }>) => (
  <QueryErrorResetBoundary>
    {({ reset }) => (
      <ErrorBoundary
        onReset={reset}
        fallbackRender={(props) => ApiFallbackRender(props, errorComponent)}
      >
        {children}
      </ErrorBoundary>
    )}
  </QueryErrorResetBoundary>
);

const GlobalErrorBoundary = ({ children }: PropsWithChildren) => (
  <QueryErrorResetBoundary>
    {({ reset }) => (
      <ErrorBoundary onReset={reset} fallbackRender={GlobalFallbackRender}>
        {children}
      </ErrorBoundary>
    )}
  </QueryErrorResetBoundary>
);

// NOTE example
// const ParentComp = () => {
//   return (
//     <ApiErrorBoundary>
//       <Suspense>
//         <ChildComp />
//       </Suspense>
//     </ApiErrorBoundary>
//   )
// }

// const ChildComp = () => {
//   const { data } = useFetch();
//   return <div>{data.title}</div>
// }

export { GlobalErrorBoundary, ApiErrorBoundary };
